/**
 * @fileoverview Login Page page
 * This page provides a login interface with Google authentication.
 *
 * @module LoginPage
 */

import {useEffect, useState} from "react";
import {Button, Col, Row} from "antd";
import {useNavigate} from "react-router-dom";
import golden from "../assets/images/golden.webp";
import "../assets/styles/LoginPage.css";
import {doSignInWithGoogle} from "../firebase/auth";
import {useAuth} from "../contexts/authContext";
import {createUser} from "../services/AuthServices";
import {useTranslation} from "react-i18next";

/**
 * LoginPage page that provides Google authentication
 *
 * @returns {JSX.Element} LoginPage page
 */
const LoginPage = () => {
    const {t} = useTranslation();
    const {userLoggedIn} = useAuth();
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const navigate = useNavigate();

    /**
     * Check if user is already logged in and redirect if necessary
     */
    useEffect(() => {
        const checkAuth = () => {
            if (userLoggedIn) {
                navigate("/dashboard");
            } else {
                setIsCheckingAuth(false);
            }
        };
        checkAuth();
    }, [navigate, userLoggedIn]);

    /**
     * Handle Google sign-in process
     *
     * @returns {Promise<void>} Promise that resolves when sign-in is complete
     */
    const Login = async () => {
        if (isSigningIn) return;
        setIsSigningIn(true);

        try {
            const result = await doSignInWithGoogle();
            const user = result.user;

            const idToken = await user.getIdToken(true);

            await createUser(
                user.email,
                result._tokenResponse.firstName,
                result._tokenResponse.lastName,
                user.photoURL,
                idToken
            );

            navigate("/dashboard");
        } catch (error) {
            console.error("Error signing in:", error);
            setIsSigningIn(false);
        }
    };

    if (isCheckingAuth) {
        return <div>{t("loginPage.verifyingAuth")}</div>; // Mostrar un indicador de carga
    }

    return (
        <div className="body">
            <Row className="content">
                <Col lg={10} sm={24} xs={24} className="newleftside">
                    <div>
                        <img src={golden || "/placeholder.svg"} alt="ovejero" className="dogimage2"/>
                    </div>
                </Col>
                <Col lg={14} sm={24} xs={24} className="rightside">
                    <Col span={24}>
                        <Col span={24} className="rightside-container2">
                            <div className="logo-container spacing2">
                                <p className="bluetitle decrease">{t("loginPage.startYourStory")}</p>
                            </div>
                            <div className="button-container">
                                <Button className="google-login-button" onClick={Login}>
                                    <img
                                        src="https://developers.google.com/identity/images/g-logo.png"
                                        alt="Google Logo"
                                        className="google-logo"
                                    />
                                    <span className="google-login-text">{t("loginPage.signInWithGoogle")}</span>
                                </Button>
                            </div>
                        </Col>
                    </Col>
                </Col>
            </Row>
        </div>
    );
};

export default LoginPage;

